/**
 * دروازه‌ی ورود پنل مدیریت.
 *
 * از دروازه‌ی نسخه‌ی مشتری (`AccessGate`) **جدا** است، هم در ظاهر و هم در رمز.
 * دلیل جدا بودن رمز، امنیتی است: پنل به کل پایگاه دانش، کلید API و انتشار
 * دسترسی دارد؛ دادن دسترسی مشاوره به یک مشتری نباید دسترسی پنل بدهد. مقایسه‌ی
 * رمز سمت سرور و بر اساس حالت اجرا انجام می‌شود (`ADMIN_ACCESS_CODE`).
 *
 * ظاهرش عمداً از نسخه‌ی مشتری متفاوت است تا کاربر در یک نگاه بفهمد کدام محیط
 * را باز کرده.
 */

import { useMutation } from '@tanstack/react-query'
import { KeyRound, Lock, ShieldCheck, SlidersHorizontal, Sparkles } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Banner } from '@/components/ui/primitives'
import { ApiError } from '@/lib/api/client'
import { createSession } from '@/lib/api/endpoints'
import { useMeta } from '@/lib/meta-context'

export const AdminGate = () => {
  const { signIn } = useMeta()
  const [code, setCode] = useState('')

  const login = useMutation({
    mutationFn: (accessCode: string) => createSession(accessCode),
    onSuccess: (session) => {
      signIn(session.token)
    },
  })

  const error = login.error
  const message = error instanceof ApiError ? error.message : null
  const isLocked = error instanceof ApiError && error.code === 'AccessLocked'

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden p-6">
      {/* زمینه‌ی تیره و شبکه‌ی نقطه‌چین: امضای بصری محیط مدیریت. */}
      <div
        className="absolute inset-0 bg-[linear-gradient(160deg,var(--t-rail-from),var(--t-rail-to))]"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute -top-40 start-1/4 size-[36rem] rounded-full bg-accent/20 blur-3xl"
        aria-hidden
      />
      <span
        className="dot-grid pointer-events-none absolute inset-0 opacity-[0.06]"
        aria-hidden
      />

      <div className="animate-rise relative w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <span
            className="grid size-12 place-items-center rounded-md bg-linear-100 from-accent to-accent-two text-white shadow-glow ring-1 ring-inset ring-white/20"
            aria-hidden
          >
            <Sparkles className="size-6" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-white">
            پنل مدیریت دستیار تصمیم
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-rail-ink-faint">
            محیط کارگاه: سازمان‌ها، دستیارها، پرونده‌های تصمیم و انتشار.
          </p>
        </div>

        <div className="rounded-lg border border-white/10 bg-surface-raised/95 p-6 shadow-overlay backdrop-blur-xl">
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (code.trim() !== '') {
                login.mutate(code)
              }
            }}
          >
            <div className="space-y-2">
              <label htmlFor="admin-code" className="text-xs font-semibold text-ink">
                رمز مدیریت
              </label>
              <div className="relative">
                <span
                  className="pointer-events-none absolute inset-y-0 start-0 grid w-11 place-items-center text-ink-faint"
                  aria-hidden
                >
                  <Lock className="size-4" />
                </span>
                <input
                  id="admin-code"
                  type="password"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value)
                  }}
                  autoComplete="current-password"
                  autoFocus
                  placeholder="••••••••"
                  className="h-12 w-full rounded-md border border-line-strong bg-surface-raised ps-11 pe-3.5 text-sm text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
                  aria-invalid={message !== null}
                  {...(message === null ? {} : { 'aria-describedby': 'admin-error' })}
                />
              </div>
            </div>

            {message !== null && (
              <div id="admin-error">
                <Banner tone={isLocked ? 'warning' : 'danger'}>{message}</Banner>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              icon={<KeyRound className="size-4" aria-hidden />}
              loading={login.isPending}
              disabled={code.trim() === '' || isLocked}
            >
              ورود به پنل
            </Button>
          </form>

          <ul className="mt-6 space-y-2.5 border-t border-line pt-5 text-xs leading-relaxed text-ink-muted">
            <li className="flex gap-2.5">
              <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden />
              رمز پنل از رمز نسخه‌ی مشتری جداست. با رمز مشتری این صفحه باز نمی‌شود.
            </li>
            <li className="flex gap-2.5">
              <SlidersHorizontal className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden />
              پس از پنج تلاش ناموفق، دسترسی از همان مبدأ برای چند دقیقه بسته می‌شود.
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
